import React from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { setCurrentTerms, allowedCurrentTerms } from '@src/features/auth';
import useTranslate from '@src/i18n/useTranslate';
import CloseIcon from '@src/icons/close-icon.svg';
import { Heading6 } from '@src/fonts/typography/Typography';
import { LayoutColors } from '@src/core/theme';
import { device } from '@src/common/breakpoints';
import { AgreementDocument, AgreementComponent, AgreementElement } from '@src/core/buyerApiClient';

interface TermsModalProps {
  currentTerm: allowedCurrentTerms;
  document: AgreementDocument;
}

const TermsModal = ({ document, currentTerm }: TermsModalProps) => {
  const dispatch = useDispatch();
  const { i18n } = useTranslate();

  const handleCloseModal = () => dispatch(setCurrentTerms(null));

  return (
    <TermContainer>
      <TermHeader>
        <TermTitle>{i18n(`${currentTerm!}`)}</TermTitle>
        <TermCloseButton onClick={handleCloseModal}>
          <CloseIcon />
        </TermCloseButton>
      </TermHeader>
      <TermBody>
        {document.content.components.map((docComponent, idx) => (
          <DocComponent key={idx} component={docComponent} />
        ))}
      </TermBody>
    </TermContainer>
  );
};

interface DocComponentProps {
  component: AgreementComponent;
}

export const DocComponent = ({ component }: DocComponentProps) => {
  if (component.namedStyleType == 'TITLE') {
    return null;
  }

  const children = component.elements.map((docElement, idx) => <DocElement key={idx} element={docElement} />);

  let tagName = 'p';
  const props = {};

  if (component.namedStyleType == 'HEADING_1') {
    tagName = 'h2';
  }

  if (component.listId) {
    children.unshift(<span>- </span>);
  }

  return React.createElement(tagName, props, children);
};

interface DocElementProps {
  element: AgreementElement;
}

const DocElement = ({ element }: DocElementProps) => {
  let tagName = element.bold ? 'strong' : 'span';
  const props: { [key: string]: string } = {};

  if (element.underline) {
    props['className'] = '';
  }

  if (element.linkUrl) {
    tagName = 'a';
    props['href'] = element.linkUrl;
    props['target'] = '_blank';
  }

  return React.createElement(tagName, props, element.text);
};

const TermContainer = styled.div`
  display: flex;
  flex-direction: column;
  background: ${LayoutColors.whiteDark};
  border-radius: 8px;
  max-height: 90%;
  max-width: 90%;
  ${device.mobile} {
    max-height: 80%;
    max-width: 80%;
  }
  padding: 24px;
  margin: 5% auto;
`;

const TermHeader = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 32px;
`;

const TermCloseButton = styled.button`
  display: inline-block;
  border: none;
  width: 32px;
  height: 32px;
  cursor: pointer;
  background: none;
`;

const TermTitle = styled.h1`
  ${Heading6};
`;
const TermBody = styled.div`
  overflow-y: scroll;
`;

export default TermsModal;
